import { useEffect, useRef } from "react";

/**
 * useScrollMemory
 * Remembers the scroll position of the grid wrapper per sheet, so
 * switching tabs restores the position the user was last looking at.
 * Also accepts a "pending scroll target" used by the hyperlink hook
 * to land on a specific cell after a programmatic sheet switch.
 */
export default function useScrollMemory({ gridWrapperRef, activeSheet }) {
  const scrollBySheetRef = useRef({});
  const pendingLinkScrollRef = useRef(null);

  // Save scroll position whenever the user scrolls.
  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el || !activeSheet) return undefined;
    const onScroll = () => {
      scrollBySheetRef.current[activeSheet] = { left: el.scrollLeft, top: el.scrollTop };
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeSheet, gridWrapperRef]);

  // Restore (or apply a pending link target) on sheet change.  Two RAFs
  // are needed because the table may need an extra frame to fully lay out
  // after a sheet swap before scrollTop/Left take effect.
  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el || !activeSheet) return;
    const pendingScroll = pendingLinkScrollRef.current;
    const pos = pendingScroll || scrollBySheetRef.current[activeSheet];
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!pos) { el.scrollLeft = 0; el.scrollTop = 0; }
        else { el.scrollLeft = pos.left || 0; el.scrollTop = pos.top || 0; }
        pendingLinkScrollRef.current = null;
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [activeSheet, gridWrapperRef]);

  return { scrollBySheetRef, pendingLinkScrollRef };
}
